import React from "react";
import ReactDOM from "react-dom/client";
import {
  AppIdentifier,
  IInitializeParameters,
  IncomingMessage,
  IncomingMessageHandler,
  IncomingMessageType,
  IOpenModalParameters,
  IOpenOnboardingModalParameters,
  ISetFiltersParameters,
  ISetJWTParameters,
  ISetNavigateParameters,
  ISetUserOnboardingJWTParameters,
  IZeroHashSDK,
} from "./types";
import { dispatchActionBasedOnAppIdentifier, generateRootID } from "./utils";
import { DEFAULT_ZH_APPS_URL } from "./constants";
import AppContainer from "./iframe-container/AppContainer";
import { Provider } from "react-redux";
import store from "./redux/store";
import { decodeJWT } from "./api/convert-token";
import {
  ACTION_SET_FILTERS,
  ACTION_SET_JWT,
  ACTION_SET_NAVIGATE,
  ACTION_SET_USE_AUTH,
} from "./redux/reducers/constants";
import {
  appCompleted,
  appFailed,
  appLoaded,
  appPendingApproval,
  closeModal,
  openModal,
} from "./redux/actions";

let _zeroHashAppsURL: string = DEFAULT_ZH_APPS_URL;
let _zeroHashAppsOrigin: string = "";

/**
 * appsMessageHandlers are the message handlers that
 * handle incoming messages from app's iframes.
 * @private - not meant to be invoked publicly
 */
const appsMessageHandlers: {
  [messageType in IncomingMessageType]: IncomingMessageHandler;
} = {
  // Onboarding message handlers
  [IncomingMessageType.OnboardingAppLoaded]: () => {
    appLoaded(AppIdentifier.ONBOARDING);
  },
  [IncomingMessageType.OnboardingCloseButtonClicked]: () => {
    closeModal(AppIdentifier.ONBOARDING);
  },
  [IncomingMessageType.OnboardingPendingApproval]: () => {
    appPendingApproval(AppIdentifier.ONBOARDING);
  },
  [IncomingMessageType.OnboardingCompleted]: () => {
    appCompleted(AppIdentifier.ONBOARDING);
  },
  [IncomingMessageType.OnboardingFailed]: () => {
    appFailed(AppIdentifier.ONBOARDING);
  },
  // Crypto Withdrawals message handlers
  [IncomingMessageType.CryptoWithdrawalsCloseButtonClicked]: () => {
    closeModal(AppIdentifier.CRYPTO_WITHDRAWALS);
  },
  [IncomingMessageType.CryptoWithdrawalsAppLoaded]: () => {
    appLoaded(AppIdentifier.CRYPTO_WITHDRAWALS);
  },
  [IncomingMessageType.CryptoWithdrawalsCompleted]: () => {
    appCompleted(AppIdentifier.CRYPTO_WITHDRAWALS);
  },
  [IncomingMessageType.CryptoWithdrawalsFailed]: () => {
    appFailed(AppIdentifier.CRYPTO_WITHDRAWALS);
  },
  // Fiat Deposits message handlers
  [IncomingMessageType.FiatDepositsCloseButtonClicked]: () => {
    closeModal(AppIdentifier.FIAT_DEPOSITS);
  },
  [IncomingMessageType.FiatDepositsAppLoaded]: () => {
    appLoaded(AppIdentifier.FIAT_DEPOSITS);
  },
  [IncomingMessageType.FiatDepositsCompleted]: () => {
    appCompleted(AppIdentifier.FIAT_DEPOSITS);
  },
  [IncomingMessageType.FiatDepositsFailed]: () => {
    appFailed(AppIdentifier.FIAT_DEPOSITS);
  },
  // Fiat Withdrawals message handlers
  [IncomingMessageType.FiatWithdrawalsCloseButtonClicked]: () => {
    closeModal(AppIdentifier.FIAT_WITHDRAWALS);
  },
  [IncomingMessageType.FiatWithdrawalsAppLoaded]: () => {
    appLoaded(AppIdentifier.FIAT_WITHDRAWALS);
  },
  [IncomingMessageType.FiatWithdrawalsCompleted]: () => {
    appCompleted(AppIdentifier.FIAT_WITHDRAWALS);
  },
  [IncomingMessageType.FiatWithdrawalsFailed]: () => {
    appFailed(AppIdentifier.FIAT_WITHDRAWALS);
  },
  // Crypto buy message handlers
  [IncomingMessageType.CryptoBuyCloseButtonClicked]: () => {
    closeModal(AppIdentifier.CRYPTO_BUY);
  },
  [IncomingMessageType.CryptoBuyAppLoaded]: () => {
    appLoaded(AppIdentifier.CRYPTO_BUY);
  },
  [IncomingMessageType.CryptoBuyCompleted]: () => {
    appCompleted(AppIdentifier.CRYPTO_BUY);
  },
  [IncomingMessageType.CryptoBuyFailed]: () => {
    appFailed(AppIdentifier.CRYPTO_BUY);
  },
  // Crypto sell message handlers
  [IncomingMessageType.CryptoSellCloseButtonClicked]: () => {
    closeModal(AppIdentifier.CRYPTO_SELL);
  },
  [IncomingMessageType.CryptoSellAppLoaded]: () => {
    appLoaded(AppIdentifier.CRYPTO_SELL);
  },
  [IncomingMessageType.CryptoSellCompleted]: () => {
    appCompleted(AppIdentifier.CRYPTO_SELL);
  },
  [IncomingMessageType.CryptoSellFailed]: () => {
    appFailed(AppIdentifier.CRYPTO_SELL);
  },
  // Fund message handlers
  [IncomingMessageType.FundAppLoaded]: () => {
    appLoaded(AppIdentifier.FUND);
  },
  [IncomingMessageType.FundCloseButtonClicked]: () => {
    closeModal(AppIdentifier.FUND);
  },
  [IncomingMessageType.FundCompleted]: () => {
    appCompleted(AppIdentifier.FUND);
  },
  [IncomingMessageType.FundFailed]: () => {
    appFailed(AppIdentifier.FUND);
  },
  [IncomingMessageType.FundError]: () => {
    appFailed(AppIdentifier.FUND);
  },
  [IncomingMessageType.FundDepositSubmitted]: () => {
    appCompleted(AppIdentifier.FUND);
  },
  [IncomingMessageType.FundConnectDeposit]: () => {
    // Handler for Connect-specific deposit event
    appCompleted(AppIdentifier.FUND);
  },
  [IncomingMessageType.FundConnectEvent]: () => {
    // Handler for catch-all Connect Auth events
    // No specific action needed
  },
  // Profile message handlers
  [IncomingMessageType.ProfileAppLoaded]: () => {
    appLoaded(AppIdentifier.PROFILE);
  },
  [IncomingMessageType.ProfileCloseButtonClicked]: () => {
    closeModal(AppIdentifier.PROFILE);
  },
  [IncomingMessageType.ProfileFailed]: () => {
    appFailed(AppIdentifier.PROFILE);
  },
  // Crypto Account Link message handlers
  [IncomingMessageType.CryptoAccountLinkAppLoaded]: () => {
    appLoaded(AppIdentifier.CRYPTO_ACCOUNT_LINK);
  },
  [IncomingMessageType.CryptoAccountLinkCloseButtonClicked]: () => {
    closeModal(AppIdentifier.CRYPTO_ACCOUNT_LINK);
  },
  [IncomingMessageType.CryptoAccountLinkFailed]: () => {
    appFailed(AppIdentifier.CRYPTO_ACCOUNT_LINK);
  },
  [IncomingMessageType.CryptoAccountLinkCompleted]: () => {
    appCompleted(AppIdentifier.CRYPTO_ACCOUNT_LINK);
  },
  // Crypto Account Link Payouts message handlers
  [IncomingMessageType.CryptoAccountLinkPayoutsAppLoaded]: () => {
    appLoaded(AppIdentifier.CRYPTO_ACCOUNT_LINK_PAYOUTS);
  },
  [IncomingMessageType.CryptoAccountLinkPayoutsCloseButtonClicked]: () => {
    closeModal(AppIdentifier.CRYPTO_ACCOUNT_LINK_PAYOUTS);
  },
  [IncomingMessageType.CryptoAccountLinkPayoutsFailed]: () => {
    appFailed(AppIdentifier.CRYPTO_ACCOUNT_LINK_PAYOUTS);
  },
  [IncomingMessageType.CryptoAccountLinkPayoutsCompleted]: () => {
    appCompleted(AppIdentifier.CRYPTO_ACCOUNT_LINK_PAYOUTS);
  },
  // Payouts message handlers
  [IncomingMessageType.PayoutsAppLoaded]: () => {
    appLoaded(AppIdentifier.PAYOUTS);
  },
  [IncomingMessageType.PayoutsCloseButtonClicked]: () => {
    closeModal(AppIdentifier.PAYOUTS);
  },
  [IncomingMessageType.PayoutsCompleted]: () => {
    appCompleted(AppIdentifier.PAYOUTS);
  },
  [IncomingMessageType.PayoutsFailed]: () => {
    appFailed(AppIdentifier.PAYOUTS);
  },
  // Pay message handlers
  [IncomingMessageType.PayAppLoaded]: () => {
    appLoaded(AppIdentifier.PAY);
  },
  [IncomingMessageType.PayCloseButtonClicked]: () => {
    closeModal(AppIdentifier.PAY);
  },
  [IncomingMessageType.PayCompleted]: () => {
    appCompleted(AppIdentifier.PAY);
  },
  [IncomingMessageType.PayFailed]: () => {
    appFailed(AppIdentifier.PAY);
  },
  // Fiat Account Link message handlers
  [IncomingMessageType.FiatAccountLinkAppLoaded]: () => {
    appLoaded(AppIdentifier.FIAT_ACCOUNT_LINK);
  },
  [IncomingMessageType.FiatAccountLinkCloseButtonClicked]: () => {
    closeModal(AppIdentifier.FIAT_ACCOUNT_LINK);
  },
  [IncomingMessageType.FiatAccountLinkCompleted]: () => {
    appCompleted(AppIdentifier.FIAT_ACCOUNT_LINK);
  },
  [IncomingMessageType.FiatAccountLinkFailed]: () => {
    appFailed(AppIdentifier.FIAT_ACCOUNT_LINK);
  },
  // StyleConfig is handled by the AppContainer component via postMessage
  [IncomingMessageType.StyleConfig]: () => {
    // No action needed - styles are applied directly in AppContainer
  },
};

/**
 * messageRouter routes the message to the appropriate message handler based on the host
 * @private - not meant to be invoked publicly
 */
const messageRouter = (event: MessageEvent): void => {
  const isSameOrigin = window.location.origin === event.origin;
  const isZeroHashOrigin = event.origin === _zeroHashAppsOrigin;

  // Check if Fund is using Auth (for same-origin messages)
  const useAuth = store.getState().fund?.useAuth || false;

  // Accept messages from iframe (_zeroHashAppsOrigin) OR from Auth component (same origin + useAuth enabled)
  if (isZeroHashOrigin || (isSameOrigin && useAuth)) {
    const incomingMessage: IncomingMessage = event.data;
    if (
      incomingMessage &&
      incomingMessage.type &&
      appsMessageHandlers[incomingMessage.type]
    ) {
      try {
        appsMessageHandlers[incomingMessage.type](incomingMessage.payload);
      } catch (e) {
        console.error(e);
        // TODO: emit event that informs the platform of failure event, to inform ZH of bug
      }
    }
  }
};

export class ZeroHashSDK implements IZeroHashSDK {
  private rootQuerySelector: string = "";
  private onboardingInitialized: boolean = false;
  /**
   * initializedApps is a map that keeps track of which apps have been initialized.
   * This might grow as we start supporting new apps.
   */
  private initializedApps: Map<AppIdentifier, boolean> = new Map([
    [AppIdentifier.CRYPTO_WITHDRAWALS, false],
    [AppIdentifier.ONBOARDING, false],
    [AppIdentifier.FIAT_DEPOSITS, false],
    [AppIdentifier.FIAT_WITHDRAWALS, false],
    [AppIdentifier.CRYPTO_BUY, false],
    [AppIdentifier.CRYPTO_SELL, false],
    [AppIdentifier.FUND, false],
    [AppIdentifier.PROFILE, false],
    [AppIdentifier.CRYPTO_ACCOUNT_LINK, false],
    [AppIdentifier.CRYPTO_ACCOUNT_LINK_PAYOUTS, false],
    [AppIdentifier.PAYOUTS, false],
    [AppIdentifier.PAY, false],
    [AppIdentifier.FIAT_ACCOUNT_LINK, false],
  ]);

  /**
   * Sets up the ZeroHash SDK and appends the ZeroHash DOM elements onto the page.
   *
   * For more information, see {@code IInitializeParameters}
   */
  constructor({
    zeroHashOnboardingURL = DEFAULT_ZH_APPS_URL,
    rootQuerySelector,
    userOnboardingJWT,
    cryptoWithdrawalsJWT,
    fiatDepositsJWT,
    fiatWithdrawalsJWT,
    cryptoBuyJWT,
    cryptoSellJWT,
    fundJWT,
    profileJWT,
    cryptoAccountLinkJWT,
    cryptoAccountLinkPayoutsJWT,
    payoutsJWT,
    payJWT,
    fiatAccountLinkJWT,
    zeroHashAppsURL,
  }: IInitializeParameters) {
    // The || is for backwards compatibility
    _zeroHashAppsURL = zeroHashAppsURL || zeroHashOnboardingURL;
    const onboardingURL = new URL(_zeroHashAppsURL);
    _zeroHashAppsOrigin = onboardingURL.origin;
    if (rootQuerySelector) {
      this.rootQuerySelector = rootQuerySelector as string;
    }

    // register message handler
    window.addEventListener("message", messageRouter, false);

    if (userOnboardingJWT) {
      this.setJWT({
        jwt: userOnboardingJWT,
        appIdentifier: AppIdentifier.ONBOARDING,
      });
    }
    if (cryptoWithdrawalsJWT) {
      this.setJWT({
        jwt: cryptoWithdrawalsJWT,
        appIdentifier: AppIdentifier.CRYPTO_WITHDRAWALS,
      });
    }
    if (fiatDepositsJWT) {
      this.setJWT({
        jwt: fiatDepositsJWT,
        appIdentifier: AppIdentifier.FIAT_DEPOSITS,
      });
    }
    if (fiatWithdrawalsJWT) {
      this.setJWT({
        jwt: fiatWithdrawalsJWT,
        appIdentifier: AppIdentifier.FIAT_WITHDRAWALS,
      });
    }
    if (cryptoBuyJWT) {
      this.setJWT({
        jwt: cryptoBuyJWT,
        appIdentifier: AppIdentifier.CRYPTO_BUY,
      });
    }
    if (cryptoSellJWT) {
      this.setJWT({
        jwt: cryptoSellJWT,
        appIdentifier: AppIdentifier.CRYPTO_SELL,
      });
    }
    if (fundJWT) {
      this.setJWT({
        jwt: fundJWT,
        appIdentifier: AppIdentifier.FUND,
      });
    }
    if (profileJWT) {
      this.setJWT({
        jwt: profileJWT,
        appIdentifier: AppIdentifier.PROFILE,
      });
    }
    if (cryptoAccountLinkJWT) {
      this.setJWT({
        jwt: cryptoAccountLinkJWT,
        appIdentifier: AppIdentifier.CRYPTO_ACCOUNT_LINK,
      });
    }
    if (cryptoAccountLinkPayoutsJWT) {
      this.setJWT({
        jwt: cryptoAccountLinkPayoutsJWT,
        appIdentifier: AppIdentifier.CRYPTO_ACCOUNT_LINK_PAYOUTS,
      });
    }
    if (payoutsJWT) {
      this.setJWT({
        jwt: payoutsJWT,
        appIdentifier: AppIdentifier.PAYOUTS,
      });
    }
    if (payJWT) {
      this.setJWT({
        jwt: payJWT,
        appIdentifier: AppIdentifier.PAY,
      });
    }
    if (fiatAccountLinkJWT) {
      this.setJWT({
        jwt: fiatAccountLinkJWT,
        appIdentifier: AppIdentifier.FIAT_ACCOUNT_LINK,
      });
    }
  }

  /**
   * setJWT sets the JWT for the appIdentifier provided.
   * The JWT should be the JWT provided by ZeroHash via the platform
   * API proxied through your servers. As ZeroHash cannot authenticate
   * your users' requests, it is paramount that the user be authenticated
   * and validated on your servers, and exchanged for the JWT using your
   * API key. DO NOT have the JWT exchange logic be on your front-end.
   *
   * As a precaution, we may restrict traffic to the JWT exchange API to
   * whitelisted IPs that come from your server.
   */
  setJWT({ jwt, appIdentifier }: ISetJWTParameters): void {
    dispatchActionBasedOnAppIdentifier(appIdentifier, ACTION_SET_JWT, { jwt });
  }
  setFilters({ filters, appIdentifier }: ISetFiltersParameters): void {
    dispatchActionBasedOnAppIdentifier(appIdentifier, ACTION_SET_FILTERS, {
      filters,
    });
  }
  /**
   * Sets the navigate field for the appIdentifier provided. Currently this is
   * specific to Onboarding and is used to navigate to a specific page within the App.
   */
  setNavigate({ appIdentifier, navigate }: ISetNavigateParameters): void {
    dispatchActionBasedOnAppIdentifier(appIdentifier, ACTION_SET_NAVIGATE, {
      navigate,
    });
  }
  /**
   * setUserOnboardingJWT sets the JWT to be whatever value is provided.
   * The JWT should be the UserJWT provided by ZeroHash via the platform
   * API proxied through your servers. As ZeroHash cannot authenticate
   * your users' requests, it is paramount that the user be authenticated
   * and validated on your servers, and exchanged for the JWT using your
   * API key. DO NOT have the JWT exchange logic be on your front-end.
   *
   * As a precaution, we may restrict traffic to the JWT exchange API to
   * whitelisted IPs that come from your server.
   * @deprecated in favor of setJWT({jwt: <userOnboardingJWT>, appIdentifier: "onboarding"})
   */
  setUserOnboardingJWT(params: ISetUserOnboardingJWTParameters): void {
    this.setJWT({
      jwt: params.userOnboardingJWT,
      appIdentifier: AppIdentifier.ONBOARDING,
    });
  }

  /**
   * isOnboardingModalOpen returns true if the onboarding modal is open,
   * false otherwise
   * @deprecated in favor of isModalOpen("onboarding")
   */
  isOnboardingModalOpen(): boolean {
    return store.getState().onboarding.isAppActive;
  }

  /**
   * isModalOpen returns true if the modal is open,
   * for the appIdentifier provided, false otherwise
   */
  isModalOpen(appIdentifier: AppIdentifier): boolean {
    return store.getState()[appIdentifier].isAppActive;
  }

  /**
   * closeModal hides the modal for the appIdentifier provided.
   */
  closeModal(appIdentifier: AppIdentifier): void {
    closeModal(appIdentifier);
  }

  /**
   * openModal opens the modal for the appIdentifier provided.
   */
  openModal({
    jwt,
    appIdentifier,
    filters,
    navigate,
  }: IOpenModalParameters): void {
    if (filters) {
      this.setFilters({ filters, appIdentifier });
    }
    if (navigate) {
      this.setNavigate({ appIdentifier, navigate });
    }

    // Check if we should use Auth based on JWT issuer
    let useAuth = false;

    if (jwt) {
      const payload = decodeJWT(jwt);

      // If JWT has 'iss' field containing 'connect', use Auth instead of iframe
      if (payload && payload.iss && typeof payload.iss === 'string' && payload.iss.includes('connect')) {
        useAuth = true;
      }
    }

    if (jwt) {
      this.setJWT({
        jwt,
        appIdentifier,
      });
    }
    if (appIdentifier === AppIdentifier.FUND) {
      dispatchActionBasedOnAppIdentifier(appIdentifier, ACTION_SET_USE_AUTH, { useAuth });
    }

    openModal(appIdentifier);
    if (this.initializedApps.get(appIdentifier)) {
      // if the App was previously initialized already,
      // simply open the modal, do not re-inject.
      return;
    }

    let root: HTMLElement | null = null;
    if (this.rootQuerySelector) {
      root = document.querySelector(this.rootQuerySelector);
    }

    // Previously this was if (!root) {...}
    if (!this.initializedApps.get(appIdentifier)) {
      // Create the default ZeroHash div, e.g. <div id="zerohash-<APP_IDENTIFIER>-49vt8y"/>
      root = document.createElement("div");
      const rootID = generateRootID(appIdentifier);
      root.id = rootID;
      root.style.position = "absolute";
      root.style.top = "0";
      root.style.left = "0";
      this.rootQuerySelector = `#${rootID}`;

      // Append the ZeroHash div as a child of <body>
      const destinationBox = document.querySelector("body") as HTMLBodyElement;
      destinationBox.appendChild(root);
    }

    if (root) {
      // Create a shadow wrapper around ZeroHash app for encapsulation
      const shadowRoot = root.attachShadow({ mode: "closed" });

      // Render the React modal
      const urlWithIdentifier = new URL(appIdentifier, _zeroHashAppsURL).href;
      ReactDOM.createRoot(shadowRoot).render(
        <Provider store={store}>
          <AppContainer
            appIdentifier={appIdentifier}
            zeroHashAppURL={urlWithIdentifier}
            useAuth={useAuth}
          />
        </Provider>
      );

      // set as initialized
      this.initializedApps.set(appIdentifier, true);
    } else {
      const errMsg =
        "failed to append ZeroHash root to the page: root not found";
      console.error(errMsg);
      throw new Error(errMsg);
    }
  }

  /**
   * openOnboardingModal opens the onboarding modal
   * @deprecated in favor of openModal({appIdentifier: "onboarding", jwt: <userOnboardingJWT>})
   */
  openOnboardingModal(params: IOpenOnboardingModalParameters): void {
    if (params.userOnboardingJWT) {
      this.setUserOnboardingJWT({
        userOnboardingJWT: params.userOnboardingJWT,
      });
    }
    openModal(AppIdentifier.ONBOARDING);
    if (this.onboardingInitialized) {
      // if it was previously initialized already,
      // simply open the modal, do not re-inject.
      return;
    }
    let root: HTMLElement | null = null;
    if (this.rootQuerySelector) {
      root = document.querySelector(this.rootQuerySelector);
    }

    if (!root) {
      // Create the default ZeroHash div, e.g. <div id="zerohash-49vt8y"/>
      root = document.createElement("div");
      const rootID = generateRootID();
      root.id = rootID;
      root.style.position = "absolute";
      root.style.top = "0";
      root.style.left = "0";
      this.rootQuerySelector = `#${rootID}`;

      // Append the ZeroHash div as a child of <body>
      const destinationBox = document.querySelector("body") as HTMLBodyElement;
      destinationBox.appendChild(root);
    }

    if (root) {
      // Create a shadow wrapper around ZeroHash app for encapsulation
      const shadowRoot = root.attachShadow({ mode: "closed" });

      // Render the React modal
      ReactDOM.createRoot(shadowRoot).render(
        <Provider store={store}>
          <AppContainer
            appIdentifier={AppIdentifier.ONBOARDING}
            zeroHashAppURL={_zeroHashAppsURL}
          />
        </Provider>
      );

      // set as initialized
      this.onboardingInitialized = true;
    } else {
      const errMsg =
        "failed to append ZeroHash root to the page: root not found";
      console.error(errMsg);
      throw new Error(errMsg);
    }
  }

  /**
   * closeOnboardingModalModal hides the onboarding modal
   * @deprecated in favor of closeModal("onboarding")
   */
  closeOnboardingModal(): void {
    closeModal(AppIdentifier.ONBOARDING);
  }
}

if (window) {
  // if the window object exists, the ZeroHash SDK
  // constructor will be appended to it for easier
  // access.
  window.zerohash = ZeroHashSDK;
}

export default ZeroHashSDK;

export * from "./types";
